"use client"

import { useEditor, EditorContent } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import Image from "@tiptap/extension-image"
import Link from "@tiptap/extension-link"
import { Placeholder } from "@tiptap/extensions/placeholder"
import { useRef, useEffect } from "react"
import { Button } from "@/components/ui/button"
import {
  IconBold,
  IconItalic,
  IconList,
  IconListNumbers,
  IconLink,
  IconPhotoPlus,
} from "@tabler/icons-react"
import { cn } from "@/lib/utils"

interface EmailRichTextEditorProps {
  value: string
  onChange: (html: string) => void
  placeholder?: string
  id?: string
  className?: string
  disabled?: boolean
}

export function EmailRichTextEditor({
  value,
  onChange,
  placeholder = "Write your message…",
  id,
  className,
  disabled = false,
}: EmailRichTextEditorProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const editor = useEditor({
    extensions: [
      StarterKit,
      Image.configure({
        allowBase64: true,
        inline: false,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { target: "_blank", rel: "noopener noreferrer" },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    editable: !disabled,
    immediatelyRender: false,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML()
      // TipTap outputs <p></p> when empty; normalize to "" so validation and storage stay consistent.
      // Content that is only an image (e.g. pasted) is not empty — allow it so form can submit.
      const hasImage = /<img\s/i.test(html ?? '')
      const hasText = (html ?? '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length > 0
      const isEmpty = !html?.trim() || (!hasText && !hasImage)
      const normalized = isEmpty ? '' : html
      if (normalized !== value) onChange(normalized)
    },
  })

  // Sync when value changes from parent (e.g. switching from HTML mode)
  useEffect(() => {
    if (!editor) return
    const current = editor.getHTML()
    const currentHasImage = /<img\s/i.test(current ?? '')
    const currentHasText = (current ?? '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length > 0
    const currentEmpty = !current?.trim() || (!currentHasText && !currentHasImage)
    const valueHasImage = /<img\s/i.test(value ?? '')
    const valueHasText = (value ?? '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length > 0
    const valueEmpty = !value?.trim() || (!valueHasText && !valueHasImage)
    if (valueEmpty && currentEmpty) return // both empty, no need to set
    if (value !== current) {
      editor.commands.setContent(value || "", { emitUpdate: false })
    }
  }, [value, editor])

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled)
    }
  }, [disabled, editor])

  // Allow pasting images from clipboard into the body
  useEffect(() => {
    const el = wrapperRef.current
    if (!el || !editor) return
    const handlePaste = (e: ClipboardEvent) => {
      const item = Array.from(e.clipboardData?.items ?? []).find((item) => item.type.startsWith('image/'))
      if (!item) return
      const file = item.getAsFile()
      if (!file) return
      e.preventDefault()
      const reader = new FileReader()
      reader.onload = () => {
        const src = reader.result as string
        editor.chain().focus().setImage({ src }).run()
      }
      reader.readAsDataURL(file)
    }
    el.addEventListener('paste', handlePaste)
    return () => el.removeEventListener('paste', handlePaste)
  }, [editor])

  const addImage = () => {
    inputRef.current?.click()
  }

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !editor) return
    if (!file.type.startsWith("image/")) return
    const reader = new FileReader()
    reader.onload = () => {
      const src = reader.result as string
      editor.chain().focus().setImage({ src }).run()
    }
    reader.readAsDataURL(file)
    e.target.value = ""
  }

  const setLink = () => {
    if (!editor) return
    const url = window.prompt("URL:", editor.getAttributes("link").href || "https://")
    if (url) editor.chain().focus().setLink({ href: url }).run()
    else editor.chain().focus().unsetLink().run()
  }

  if (!editor) return null

  return (
    <div
      ref={wrapperRef}
      id={id}
      className={cn(
        "rounded-md border border-input bg-background overflow-hidden",
        disabled && "opacity-60 pointer-events-none",
        className
      )}
    >
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 border-b border-input bg-muted/40 p-1">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => editor.chain().focus().toggleBold().run()}
          data-active={editor.isActive("bold")}
        >
          <IconBold className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          data-active={editor.isActive("italic")}
        >
          <IconItalic className="h-4 w-4" />
        </Button>
        <div className="w-px h-6 bg-border mx-0.5" />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          data-active={editor.isActive("bulletList")}
        >
          <IconList className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          data-active={editor.isActive("orderedList")}
        >
          <IconListNumbers className="h-4 w-4" />
        </Button>
        <div className="w-px h-6 bg-border mx-0.5" />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={setLink}
          data-active={editor.isActive("link")}
        >
          <IconLink className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={addImage}>
          <IconPhotoPlus className="h-4 w-4" />
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          aria-hidden
          onChange={handleImageSelect}
        />
      </div>
      {/* Editor */}
      <EditorContent
        editor={editor}
        className="email-rich-text-editor-prose min-h-[220px] px-3 py-2 text-sm [&_.tiptap]:outline-none [&_.tiptap]:min-h-[200px]"
      />
    </div>
  )
}
